import type { jsPDF } from 'jspdf';

/**
 * Liberation Sans for jsPDF — metric-compatible with Helvetica, but covering Latin Extended,
 * Cyrillic and Greek. jsPDF's built-in `helvetica` is WinAnsi only, so patient names with Polish,
 * Czech, Hungarian, Turkish, Cyrillic or Greek characters render as garbage without this.
 *
 * Liberation Sans 2.1.5, SIL Open Font License 1.1 — https://github.com/liberationfonts
 *
 * LC-910.
 */
export const LIBERATION_SANS = 'LiberationSans';

const FILES = {
  normal: 'LiberationSans-Regular.ttf',
  bold: 'LiberationSans-Bold.ttf',
} as const;

// ~800 KB for both faces, so fetch on demand and keep them for later reports in the same session
const cache = new Map<string, Promise<string>>();

const toBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  // chunked because String.fromCharCode(...bytes) blows the argument limit on a 400 KB font
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const load = (file: string): Promise<string> => {
  const cached = cache.get(file);
  if (cached) {
    return cached;
  }

  const pending = fetch(`/fonts/${file}`)
    .then(response => {
      if (!response.ok) {
        throw new Error(`could not load /fonts/${file}: ${response.status}`);
      }
      return response.arrayBuffer();
    })
    .then(toBase64)
    .catch(error => {
      // let the next attempt retry instead of caching the failure
      cache.delete(file);
      throw error;
    });

  cache.set(file, pending);
  return pending;
};

/**
 * Registers both faces under one family so `fontStyle: 'bold'` resolves. Without the bold face
 * jsPDF falls back silently, which matters because the autotable header style is bold.
 */
export const registerLiberationSans = async (doc: jsPDF): Promise<void> => {
  const [regular, bold] = await Promise.all([load(FILES.normal), load(FILES.bold)]);

  doc.addFileToVFS(FILES.normal, regular);
  doc.addFont(FILES.normal, LIBERATION_SANS, 'normal');
  doc.addFileToVFS(FILES.bold, bold);
  doc.addFont(FILES.bold, LIBERATION_SANS, 'bold');
};
